import { useMemo, useState } from 'react'
import { dateFormat, flatten, numberFormat } from 'shared/util'

import { Loading } from 'components/controls/loading'
import { ErrorBlock } from 'components/controls/errorInfo'

import {
	Stack,
	ListItem,
	ListItemButton,
	Typography,
	ListItemProps,
	Paper,
	Button,
	Box,
	Divider,
} from '@mui/material'
import { ChevronRight as OpenIcon } from '@mui/icons-material'

import { MrpProjectResult, useCountMrpHaisin, useListMrpLog } from 'interface/carenet/mrp'
import { MrpLogGraph, MrpLogGraphOptions } from './mrpLogGraph'
import { SwitchBox } from 'components/controls/switchBox'

type MrpProjectRecordProps = {
	project: MrpProjectResult
	openMrpProject: (projectId: number, isOpenGrid: boolean) => void
} & ListItemProps
export const MrpProjectRecord = ({
	project,
	openMrpProject,
	...others
}: MrpProjectRecordProps) => {
	const [open, setOpen] = useState<boolean>(false)
	return (
		<ListItem {...others} disablePadding>
			<ListItemButton onClick={() => setOpen(!open)} disableGutters disableRipple sx={{ paddingX: 1 }}>
				<Stack direction="row" boxSizing="border-box" width="100%" alignItems="flex-start">
					<OpenIcon color="action" style={{
						transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
						transition: 'transform 0.2s ease-in-out'
					}} />
					<Stack direction="column" flexGrow={1} overflow="hidden" paddingX={1}>
						<Stack direction="row" spacing={1} alignItems="flex-start">
							<Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
								{dateFormat(project.startDate, { wareki: false })}
							</Typography>
							<div
								dangerouslySetInnerHTML={{
									__html: project.projectName ?? 'empty'
								}}
								style={open ? {
									flexGrow: 1
								} : {
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									flexGrow: 1
								}}
							/>
							<Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
								{project.productName}
							</Typography>
						</Stack>
						
						{open && (
							<MrpProjectDetail
								project={project}
								openMrpProject={openMrpProject}
							/>
						)}
					</Stack>
				</Stack>
			</ListItemButton>
		</ListItem>
	)
}

type MrpProjectDetailProps = {
	project: MrpProjectResult
	openMrpProject: (projectId: number, isOpenGrid: boolean) => void
}
const MrpProjectDetail = ({
	project,
	openMrpProject,
}: MrpProjectDetailProps) => {
	const [graphOptions, setGraphOptions] = useState<MrpLogGraphOptions>({
		isSummary: false
	})
	const mrpHaisinCountState = useCountMrpHaisin(project.projectId)
	const mrpLogState = useListMrpLog(project.projectId)
	const summary = useMemo(
		() => {
			if (!mrpHaisinCountState.data || !mrpLogState.data) return null
			const haisinCount = mrpHaisinCountState.data.count
			const viewCount = mrpLogState.data.list.length
			const rate = haisinCount > 0 ? (Math.floor((viewCount * 100 * 100) / haisinCount) / 100) : null
			return {
				haisinCount,
				viewCount,
				rate
			}
		},
		[mrpLogState.data, mrpHaisinCountState.data]
	)
	return (
		<Stack direction="column" width="100%" spacing={1} paddingY={0.5}>
			<Stack direction="column">
				{project.seriesName && (
					<Stack direction="row" spacing={0.5} alignItems="baseline">
						<Typography variant="caption" color="text.secondary">シリーズ：</Typography>
						<Typography variant="body2" color="text.primary">{project.seriesName}</Typography>
					</Stack>
				)}
				<Stack direction="row" spacing={0.5} alignItems="baseline">
					<Typography variant="caption" color="text.secondary">配信期間：</Typography>
					<Typography variant="body2" color="text.primary">{dateFormat(project.startDate, { wareki: true })}</Typography>
					<Typography variant="caption" color="text.secondary">～</Typography>
					<Typography variant="body2" color="text.primary">{dateFormat(project.closeDate, { wareki: true })}</Typography>
				</Stack>
				{summary && (
					<Stack direction="row" spacing={1} alignItems="center">
						<Stack direction="row" alignItems="baseline">
							<Typography variant="caption" color="text.secondary">配信対象：</Typography>
							<Typography variant="body2" color="text.primary">{numberFormat(summary.haisinCount)}</Typography>
							<Typography variant="caption" color="text.secondary">件</Typography>
						</Stack>
						<Stack direction="row" spacing={0.5} alignItems="baseline">
							<Typography variant="caption" color="text.secondary">閲覧数：</Typography>
							<Typography variant="body2" color="text.primary">{numberFormat(summary.viewCount)}</Typography>
							<Typography variant="caption" color="text.secondary">件</Typography>
						</Stack>
						{summary.rate !== null && (
							<Stack direction="row" spacing={0.5} alignItems="baseline">
								<Typography variant="caption" color="text.secondary">視聴率：</Typography>
								<Typography variant="body2" color="text.primary">{summary.rate}</Typography>
								<Typography variant="caption" color="text.secondary">%</Typography>
							</Stack>
						)}
					</Stack>
				)}
			</Stack>
			<Paper variant="outlined" sx={{ width: "100%", height: 200, marginTop: 1, position: 'relative' }}>
				{(mrpLogState.loading || mrpHaisinCountState.loading) && <Loading />}
				<ErrorBlock error={flatten([mrpLogState.error, mrpHaisinCountState.error])} />
				{(mrpLogState.data && mrpLogState.data.list.length > 0) ? (
					<Stack direction="row" flexGrow={1}>
						<Stack direction="column" padding={1} height="100%" overflow="auto">
							<SwitchBox
								label="累計"
								labelPlacement="bottom"
								size="small"
								outerProps={{ sx: { margin: 0 }}}
								checked={graphOptions.isSummary}
								onClick={(ev) => {
									ev.stopPropagation()
									setGraphOptions({
										...graphOptions,
										isSummary: graphOptions.isSummary ? false : true
									})
								}}
							/>
						</Stack>
						<Divider orientation="vertical" flexItem />
						<Box flexGrow={1} height={200} overflow="hidden">
							<MrpLogGraph
								mrpLog={mrpLogState.data.list}
								haisinCount={mrpHaisinCountState.data?.count ?? 0}
								option={graphOptions}
								height={200}
							/>
						</Box>
					</Stack>
				) : <></>}
			</Paper>

			<Stack direction="row" spacing={1} marginTop={1} justifyContent="flex-start">
				<Button
					variant="outlined"
					color="primary"
					size="small"
					onClick={(ev) => {
						ev.stopPropagation
						openMrpProject(project.projectId, false)
					}}
				>
					ダッシュボード分析
				</Button>

				<Button
					variant="outlined"
					color="primary"
					size="small"
					onClick={(ev) => {
						ev.stopPropagation()
						openMrpProject(project.projectId, true)
					}}
				>
					シート分析
				</Button>
			</Stack>
		</Stack>
	)
}
